// import components
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { renderColorReadMode } from 'components/shared/CRUD/components/InnerPage/components/readMode/readModeRenderers';
import withReadModeField from 'components/shared/CRUD/components/InnerPage/components/withReadModeField';
import { ControlledSelect } from 'components/shared';
import { Option } from 'components/shared/ControlledFields/ControlledSelect';
import {
  ColorField,
  PrivilegedEmailField,
  RequestExcludeField,
  SortOrderField
} from '../fields/BaseFields';
import {
  GridContainer,
  GridCompact6
} from 'components/shared/Grid/Grid.components';

// import hooks
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEditPageContext } from 'components/shared/CRUD/components/InnerPage/EditPageContext';
import { useSelector } from 'hooks';
// import { useMemo } from 'react';

// import queries
import { useGetBrTypeOptions, useGetBrCategoryOptions } from 'queries';

// import enums
import { PlanUnitType } from 'components/enums';

const PREFIX = 'PlanUnitFormFields';

const classes = {
  root: `${PREFIX}-root`
};

const StyledBox = styled(Box)(() => ({
  [`&.${classes.root}`]: {
    flexDirection: 'column',
    flex: 1
  }
}));

const SelectField = (props) => {
  return <ControlledSelect {...props} variant="outlined" />;
};

const TypeFieldWithReadMode = withReadModeField(SelectField);
const CategoryFieldWithReadMode = withReadModeField(SelectField);
const SortOrderFieldWithReadMode = withReadModeField(SortOrderField);
const ColorFieldWithReadMode = withReadModeField(ColorField);
const PrivilegedEmailFieldWithReadMode =
  withReadModeField(PrivilegedEmailField);

const PlanUnitFormFields = () => {
  const { isEditable } = useEditPageContext();
  const { t } = useTranslation('common');

  const { control } = useFormContext();

  const { data: brTypeOptions = [] } = useGetBrTypeOptions();
  const { data: brCategoryOptions = [] } = useGetBrCategoryOptions();
  const hasRegionalPoolOrganisationAccess = useSelector(
    (state) => state.grants.hasRegionalPoolOrganisationAccess
  );

  const filteredBrTypeOptions = hasRegionalPoolOrganisationAccess
    ? brTypeOptions
    : (brTypeOptions as Option[])?.filter(
        (option) => option.value !== PlanUnitType.REGIONAL_POOL
      );

  return (
    <StyledBox display="flex" className={classes.root}>
      <Box mb={2.5} sx={{ width: '100%' }}>
        <GridContainer mb={2.5}>
          <GridCompact6>
            <TypeFieldWithReadMode
              label={t('costCenters.grid.type')}
              name="type"
              control={control}
              options={filteredBrTypeOptions as Option[]}
              valueGetter="value"
              labelGetter="label"
            />
          </GridCompact6>

          <GridCompact6>
            <CategoryFieldWithReadMode
              label={t('costCenters.grid.category')}
              name="category"
              control={control}
              options={[
                {
                  value: '',
                  label: t('costCenters.grid.none')
                },
                ...(brCategoryOptions as Option[])
              ]}
              valueGetter="value"
              labelGetter="label"
            />
          </GridCompact6>

          <GridCompact6>
            <PrivilegedEmailFieldWithReadMode
              name="privilegedEmailAddress"
              label={t('costCenters.grid.privilegedEmailAddress')}
            />
          </GridCompact6>

          <GridCompact6>
            <RequestExcludeField disabled={!isEditable} />
          </GridCompact6>

          <GridCompact6>
            <ColorFieldWithReadMode
              label={t('color')}
              name="colorCode"
              readModeRenderer={(props) =>
                renderColorReadMode({
                  ...props
                })
              }
            />
          </GridCompact6>

          <GridCompact6>
            <SortOrderFieldWithReadMode
              name="sortOrderAgency"
              label={t('costCenters.grid.sortOrderAgency')}
            />
          </GridCompact6>
        </GridContainer>
      </Box>
    </StyledBox>
  );
};

export default PlanUnitFormFields;
